/**
 * Cliente único del endpoint público de IA de Argos (POST /ai/assist, app=hermes).
 * Una sola petición por pregunta (sin reintentos GET que gastaban el límite de 40 peticiones / 10 min),
 * tiempo máximo acotado y motivo de fallo claro para mostrar al usuario.
 */

export const ARGOS_AI_URL = "https://rycso.es/api/argos/ai/assist";

export type AiMessage = { role: "system" | "user" | "assistant"; content: string };
export type AiFailure = "busy" | "timeout" | "offline" | "unavailable";
/** ok=false ⇒ reason indica el motivo; text vacío. */
export type AiResult = { ok: boolean; text: string; reason?: AiFailure };

/** Mensaje en español, sin tecnicismos, para cada motivo de fallo. */
export function aiFailureMessage(reason: AiFailure): string {
  switch (reason) {
    case "busy":
      return "Se han hecho demasiadas preguntas a la IA seguidas. Espera un par de minutos y vuelve a intentarlo.";
    case "timeout":
      return "La IA está tardando demasiado en responder. Inténtalo de nuevo en un momento.";
    case "offline":
      return "No hay conexión con la IA. Revisa tu conexión a internet.";
    default:
      return "La IA no está disponible ahora mismo. Inténtalo más tarde.";
  }
}

/** Mientras la IA diga «demasiadas peticiones», no insistimos (evita cuelgues y gastar cupo). */
let busyUntil = 0;

export function aiCoolingDown(now = Date.now()): boolean {
  return now < busyUntil;
}

/** Solo para tests. */
export function resetAiCooldown(): void {
  busyUntil = 0;
}

export async function askArgosAi(
  messages: AiMessage[],
  opts: { timeoutMs?: number; temperature?: number } = {},
): Promise<AiResult> {
  if (aiCoolingDown()) return { ok: false, text: "", reason: "busy" };
  // Idioma elegido por el usuario (lo publica la capa i18n de la app o de la web).
  try {
    const g: any = globalThis as any;
    const l: string | undefined = g.__rycsoLang || (g.rycsoI18n && g.rycsoI18n.getLang && g.rycsoI18n.getLang());
    if (l && l !== "es") {
      messages = [
        ...messages,
        {
          role: "system",
          content: `IMPORTANT: ignore any earlier instruction to answer in Spanish. Answer in the user's language (ISO code "${l}"), keeping the same requested format (including JSON keys and the "title | detail | minutes | place" line formats unchanged).`,
        },
      ];
    }
  } catch {
    /* sin idioma: se mantiene el español */
  }
  const timeoutMs = opts.timeoutMs ?? 25000;
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // El temporizador corta la espera aunque el entorno no soporte AbortController.
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      timedOut = true;
      controller?.abort();
      reject(new Error("timeout"));
    }, timeoutMs);
  });
  try {
    const response = await Promise.race([
      fetch(`${ARGOS_AI_URL}?app=hermes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          app: "hermes",
          messages,
          ...(opts.temperature != null ? { temperature: opts.temperature } : {}),
        }),
        signal: controller?.signal,
      }),
      timeout,
    ]);
    if (response.status === 429) {
      busyUntil = Date.now() + 90_000;
      return { ok: false, text: "", reason: "busy" };
    }
    if (!response.ok) return { ok: false, text: "", reason: "unavailable" };
    const data = (await response.json()) as {
      ok?: boolean;
      content?: string;
      choices?: Array<{ message?: { content?: string } }>;
    };
    const text = String(data.content || data.choices?.[0]?.message?.content || "").trim();
    if (!text) return { ok: false, text: "", reason: "unavailable" };
    return { ok: true, text };
  } catch {
    return { ok: false, text: "", reason: timedOut ? "timeout" : "offline" };
  } finally {
    if (timer) clearTimeout(timer);
  }
}
